import { type ReactNode } from "react";
import { Icon } from "@/components/ui";

export type NavTab = "home" | "create" | "cabals" | "wallet";

interface NavItem {
    id: NavTab;
    /** Local SVG icon name (in assets/icons/) */
    icon: string;
    label: string;
}

const NAV_ITEMS: NavItem[] = [
    { id: "home", icon: "home-simple", label: "Home" },
    { id: "cabals", icon: "my-codevs", label: "My Codevs" },
    { id: "wallet", icon: "wallet-default", label: "Wallet" },
];

interface BottomNavProps {
    activeTab: NavTab;
    onTabChange?: (tab: NavTab) => void;
}

export default function BottomNav({
    activeTab,
    onTabChange,
}: BottomNavProps): ReactNode {
    return (
        <nav
            id="bottom-nav"
            className="fixed bottom-0 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center justify-end max-w-[430px] w-full h-[160px] pointer-events-none"
        >
            {/* Blurred fade — content visible but frosted behind nav */}
            <div
                aria-hidden
                className="absolute inset-0 backdrop-blur-[8px]"
                style={{
                    background: "linear-gradient(to bottom, rgba(10,10,10,0) 0%, rgba(10,10,10,0.7) 50%, rgba(10,10,10,0.85) 100%)",
                    mask: "linear-gradient(to bottom, transparent 0%, black 25%)",
                    WebkitMask: "linear-gradient(to bottom, transparent 0%, black 25%)",
                }}
            />
            <div className="relative flex items-center gap-[36px] px-[24px] py-[20px] rounded-[100px] border-[0.5px] border-white mb-[clamp(12px,1.7dvh,20px)] pointer-events-auto">
                {/* Background gradient + inner shadow */}
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none rounded-[inherit] bg-gradient-to-b from-[rgba(255,255,255,0.01)] to-transparent"
                    style={{ backgroundSize: "100% 86%" }}
                />
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none rounded-[inherit] shadow-[inset_0px_0px_9px_0px_rgba(255,255,255,0.03)]"
                />

                {NAV_ITEMS.map((item) => {
                    const isActive = activeTab === item.id;

                    return (
                        <button
                            key={item.id}
                            id={`nav-${item.id}`}
                            onClick={() => onTabChange?.(item.id)}
                            className="relative flex flex-col gap-[8px] items-center justify-center w-[69px] transition-colors"
                        >
                            <Icon
                                name={item.icon}
                                size={28}
                                inline
                                className={isActive ? "text-white" : "text-[#6E6975]"}
                            />
                            <span
                                className={`font-bold leading-[1.15] text-[14px] tracking-[-0.28px] ${
                                    isActive ? "text-white" : "text-[#6e6975]"
                                }`}
                            >
                                {item.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </nav>
    );
}
